import "core-js";
import "./assets/polyfill";
import * as serviceWorker from './serviceWorker';
import React, { useEffect } from "react";
import { render } from "react-dom";
// Redux.
import { Provider, useSelector, useDispatch } from "react-redux";
import thunk from "redux-thunk";
import { createStore, applyMiddleware, compose } from "redux";
import reducers, { initialState } from "./reducers";
// Routing.
import { BrowserRouter, Switch, Route, Redirect, useLocation } from "react-router-dom";
// Style.
import "./style/main.scss";
// Misc.
import { COOKIE_NAME } from './constants';
import { getCookies, setAffiliate, vendorPath, setCookie } from './helpers';
import { requestCurrentUser } from './actions/session';
import { Spinner } from "./assets";
import { ModalProvider } from "./assets/useModal";
// Views.
import Authenticator from './components/Authenticator';
import Vendor from './components/Vendor';
import Account from './components/Account';
import { trackPageView } from "./helpers/tracking";
import { Helmet } from "react-helmet";
import VendorOnboarding from "./components/VendorOnboarding";
import { useDeviceSize, MOBILE, DESKTOP, TABLET } from "./assets/hooks/useDeviceSize";

console.log(process.env.REACT_APP_NODE_ENV);

// Init the redux store.
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const store = createStore(
	reducers,
	initialState,
	composeEnhancers(
		applyMiddleware(thunk)
	)
);

function App() {

	const dispatch = useDispatch();
	const { ready } = useSelector(state => state.session);
	const { pathname } = useLocation();

	useEffect(() => {
		// Cookie for the last page navigated to.
		setCookie({
			key: "last_page",
			value: pathname,
		});
		// Make sure page navigation takes you to the top of the page.
		window.scrollTo(0, 0);
		// Refresh the hubspot chat widget to keep up to date with pages.
		if (window.HubSpotConversations) {
			if (!window.HubSpotConversations.widget.status().loaded) {
				window.HubSpotConversations.widget.load();
			} else {
				window.HubSpotConversations.widget.refresh();
			}
		}
		// Track page movement.
		trackPageView(pathname);
	}, [pathname]);

	// If the app starts, and there is a token in the cookie, go get the user.
	useEffect(() => {
		const cookies = getCookies();
		if (cookies[COOKIE_NAME])
			dispatch(requestCurrentUser());
	}, [dispatch]);

	// Get device width.
	const { device } = useDeviceSize();

	// We shouldn't render anything if we aren't ready.
	if (!ready) return <Spinner />;

	return (
		<div id="main" className={[
			device === MOBILE && "mobile-size",
			device === TABLET && "tablet-size",
			device === DESKTOP && "desktop-size"
		].filter(c => c).join(" ")} >

			<Helmet>
				<meta name="description" content="Making it easy for businesses to sell things, and users to buy them." />
				<meta property="og:description" content="Making it easy for businesses to sell things, and users to buy them." />
				<meta property="og:site_name" content="ORDERABL+" />
				<meta property="og:title" content="ORDERABL+ | Because selling things online should be easy." />
				<meta property="og:image" content="/media/splash_1_a.png" />
				<meta property="og:url" content="https://be.orderabl.com" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:image:alt" content="Orderable" />
			</Helmet>

			<Switch>
				<Route path="/signup" render={router => (
					<VendorOnboarding />
				)} />
				<Route path="/intro/:code" exact render={router => {
					const { code } = router.match.params;
					setAffiliate(code);
					return <Redirect to="/signup" />
				}} />
				<Route path="/auth/:provider" exact render={router => (
					<Authenticator provider={router.match.params.provider} />
				)} />
				<Route path="/me" render={router => (
					<Account router={router} />
				)} />
				<Route path="/(with|s|from|store|\+|plus)/:vendor" render={router => (
					<Vendor
						key={router.match.params.vendor}
						slug={router.match.params.vendor}
						router={router}
					/>
				)} />
				<Route path="/:vendor" render={router => (
					<Redirect to={vendorPath(router.match.params.vendor)} />
				)} />
				<Route path="/" render={router => {
					const { redirect_to } = getCookies();
					return <Redirect to={redirect_to || "/me"} />
				}} />
			</Switch>
		</div>
	);
}

render(
	<BrowserRouter>
		<Provider store={store}>
			<ModalProvider>
				<App />
			</ModalProvider>
		</Provider>
	</BrowserRouter>,
	document.getElementById("root")
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
